// Semi360 Hub SSO bridge for the ECom360 admin (additive — local login stays).
//
// On the hosted domain (ecom360.dev.semi360.ai) the admin authenticates
// against the Semi360 Hub: it reads the shared session cookie, exchanges it
// for a short-lived RS256 JWT, and stores that JWT in the SAME redux slot the
// existing login puts its token in. Every service file already reads
// state.app.token into a `token` header, so nothing downstream changes; the
// backend tells the two tokens apart by shape (AuthTokenApi's hub branch).
//
// Ported from qbuy360's src/lib/hubSso.js — the sibling that already solved
// this for a Laravel API and a CRA frontend. The one difference: qbuy360's
// token lives in localStorage, ours lives in redux-persist, so refresh hands
// the token back to the caller to dispatch rather than writing storage here.

import { createHubAuth } from "@semi360/client/hub-auth";
import { store } from "@src/store";
import { setUserTokenAction } from "@reducers/app.reducer";

export const PRODUCT_KEY = "ecom360";

/** Hub origin, derived from our own hostname so no config is needed:
 *  ecom360.<zone> -> hub.<zone>. REACT_APP_HUB_URL overrides for dev. */
export function hubBaseUrl() {
  const configured = process.env.REACT_APP_HUB_URL;
  if (configured) return configured.replace(/\/$/, "");
  const { protocol, hostname } = window.location;
  const parts = hostname.split(".");
  if (parts.length > 2) return `${protocol}//hub.${parts.slice(1).join(".")}`;
  return "https://hub.semi360.ai";
}

/** Is this the hosted Semi360 environment? On localhost the plain local
 *  login flow stays untouched for dev; REACT_APP_HUB_EMBED=1 forces the
 *  hosted flow locally so the SSO path can be exercised. */
export function hubHosted() {
  if (process.env.REACT_APP_HUB_EMBED === "1") return true;
  return /\.semi360\.ai$/i.test(window.location.hostname);
}

/** Does a stored token look like a Hub JWT (vs the local opaque token)?
 *  Three dot-separated segments is the same cheap discriminator the API uses. */
export function isHubToken(token) {
  return typeof token === "string" && token.split(".").length === 3;
}

async function getJson(path) {
  try {
    const r = await fetch(hubBaseUrl() + path, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    // Offline, CORS not configured, or no Hub in this environment. All mean
    // "fall back to local login", never "show an error".
    return null;
  }
}

/** The Hub session ({authenticated, user, org}), or null. */
export async function fetchHubSession() {
  return getJson("/api/session");
}

// Token renewal is the shared @semi360/client (hub-auth): one in-flight
// refresh, expiry-aware getToken(). The token itself stays in redux-persist
// (state.app.token), so this store reads and dispatches there; a refresh
// keeps the current user so the header avatar survives it.
const reduxTokenStore = {
  get: () => {
    const t = store.getState().app.token;
    return isHubToken(t) ? t : null;
  },
  set: (token) => {
    // null (Hub session gone) is left to the caller: it resets and redirects.
    if (token) store.dispatch(setUserTokenAction({ token, user: store.getState().app.user }));
  },
};

let auth = null;

/** The shared Hub token client, created on first use (needs window). */
export function hubAuth() {
  if (!auth) {
    auth = createHubAuth({ hub: hubBaseUrl(), product: PRODUCT_KEY, store: reduxTokenStore });
  }
  return auth;
}

/** Shape the Hub session's user like the local login's response.user, so the
 *  header avatar/name and everything reading state.app.user keeps working. */
export function hubUserShape(session) {
  const u = (session && session.user) || {};
  const org = (session && session.org) || {};
  return {
    name: u.name || u.email || "Semi360 user",
    email: u.email || "",
    role: org.role || u.role || "member",
    org: org.name || "",
    hub: true,
  };
}

export function redirectToHubSignin() {
  const next = encodeURIComponent(window.location.href);
  window.location.assign(`${hubBaseUrl()}/signin?next=${next}`);
}
